var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useThrottleCallback } from '@react-hook/throttle';
import { css, styled } from '../../theme-provider';
import { CardHeaderChevron } from './card-header-chevron';
import { CardHeaderCheckbox } from './card-header-checkbox';
import { CardTitle } from './card-title';
import { useCardContext } from '../card';
export const CardHeaderWrapper = styled.div.withConfig({ displayName: "CardHeaderWrapper", componentId: "sc-1mpflw3" }) `display:flex;justify-content:space-between;${({ multiline }) => !multiline && `align-items: center;`}:focus{outline:none;}${({ role }) => (role === 'button' || role === 'option') &&
    css `
			::after {
				content: '';
				position: absolute;
				top: 0;
				bottom: 0;
				left: 0;
				right: 0;
				cursor: pointer;
			}
		`}`;
const CardHeaderIcon = ({ dataTestId }) => {
    const { type, isActive = false } = useCardContext();
    if (type === 'collapsible') {
        return React.createElement(CardHeaderChevron, { isOpen: isActive, dataTestId: dataTestId });
    }
    if (type === 'selectable')
        return React.createElement(CardHeaderCheckbox, { isSelected: isActive, dataTestId: dataTestId });
    return null;
};
const getRoleByType = (type) => {
    if (type === 'collapsible') {
        return 'button';
    }
    if (type === 'selectable') {
        return 'option';
    }
    return undefined;
};
export const CardHeader = (_a) => {
    var { title, children } = _a, rest = __rest(_a, ["title", "children"]);
    const { type, dataTestId, isActive = false, role = getRoleByType(type), ariaLabel, } = useCardContext();
    const isSelected = type === 'selectable' ? isActive : undefined;
    const isExpanded = type === 'collapsible' ? isActive : undefined;
    const headerRef = React.useRef(null);
    const [multiline, setMultiline] = React.useState(false);
    const detectHeaderHeight = React.useCallback(() => {
        const wrapper = headerRef === null || headerRef === void 0 ? void 0 : headerRef.current;
        if (!wrapper) {
            return;
        }
        const height = wrapper.getBoundingClientRect().height;
        // magic number 38: if the height of the header is greater than 38px there are probably multiple lines of text in there. Works with emphasis "small" and "large".
        if (height > 38 !== multiline) {
            setMultiline(!multiline);
        }
    }, [multiline]);
    const handleResize = useThrottleCallback(React.useCallback(() => {
        detectHeaderHeight();
    }, [detectHeaderHeight]));
    // detect header height and multiline on window resize
    React.useEffect(() => {
        const localHandleResize = handleResize;
        window.addEventListener('resize', localHandleResize);
        return () => window.removeEventListener('resize', localHandleResize);
    }, [handleResize]);
    // initially detect header height and multiline
    React.useEffect(() => {
        handleResize();
    }, [handleResize]);
    return (React.createElement(CardHeaderWrapper, Object.assign({ "aria-label": ariaLabel, tabIndex: type !== 'default' ? 0 : undefined, role: role, "aria-selected": isSelected, "aria-expanded": isExpanded, ref: headerRef, multiline: multiline }, rest),
        React.createElement(CardTitle, { title: title }),
        React.createElement(CardHeaderIcon, { dataTestId: dataTestId }),
        children && React.createElement(React.Fragment, null, children)));
};
